import { forwardRef, type WheelEvent } from "react";
import { customColors } from "tailwind.config";

import { WarningIcon } from "$/lib/icons/WarningIcon";
import { cn } from "$/lib/utils/functions/misc.functions";

import { BaseInput } from "../../atoms";
import type { TextInputProps } from "./TextInput.types";

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      className,
      Icon,
      textIcon,
      containerClassName,
      isError,
      errorMessage = "This field is required",
      hintMessage,
      type = "text",
      iconPosition = "right",
      onKeyDown = () => undefined,
      ...props
    },
    ref,
  ) => {
    const iconPositionStyle = iconPosition === "right" ? "right-3" : "left-3";
    const paddingStyle = iconPosition === "right" ? "pr-10" : "pl-10";

    const handleWheel = (e: WheelEvent<HTMLInputElement>) => {
      // Prevent the default behavior of the mouse wheel, which increments/decrements the number input
      e.currentTarget.blur();
    };

    return (
      <div className={cn("w-full", containerClassName)}>
        {!!hintMessage &&
          (typeof hintMessage === "string" ? (
            <p className="mb-1 text-[13px] font-semibold screen850:text-sm">
              {hintMessage}
            </p>
          ) : (
            <div className="mb-1 text-[13px] font-semibold screen850:text-sm">
              {hintMessage}
            </div>
          ))}
        <div className="relative w-full">
          <div className="relative w-full">
            {Icon && (
              <div
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 transform",
                  iconPositionStyle,
                )}
              >
                <Icon />
              </div>
            )}
            {textIcon && (
              <div
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 transform",
                  iconPositionStyle,
                )}
              >
                {textIcon}
              </div>
            )}
            <BaseInput
              onWheel={handleWheel}
              className={cn(
                paddingStyle,
                className,
                (props.value ?? props.defaultValue) ? "font-semibold" : "",
              )}
              type={type}
              isError={isError}
              errorMessage={errorMessage}
              onKeyDown={(e) => {
                // disable value toggling with arrow keys
                if (e.key === "ArrowUp" || e.key === "ArrowDown")
                  e.preventDefault();
                onKeyDown(e);
              }}
              {...props}
              ref={ref}
            />
          </div>
          {isError && errorMessage && (
            <p className="mt-1 flex items-center gap-1 text-xs text-error">
              <WarningIcon
                color={customColors.error.DEFAULT}
                className="size-4"
              />
              {errorMessage}
            </p>
          )}
        </div>
      </div>
    );
  },
);

TextInput.displayName = "TextInput";

export default TextInput;
